import { useRef } from "react";
import { motion, useMotionValue, useTransform, useScroll } from "framer-motion";
import { ArrowDown, ArrowRight, Timer, CalendarCheck, Star } from "lucide-react";
import { MaskLine, Eyebrow } from "./Reveal";

const CHIPS = [
  { icon: Timer, label: "10–50 Min Sessions" },
  { icon: CalendarCheck, label: "Adapts To Your Day" },
  { icon: Star, label: "4.9/5 — 12k+ Members" },
];

const scrollTo = (href) => {
  if (window.__lenis) window.__lenis.scrollTo(href, { offset: -72, duration: 1.4 });
  else document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
};

const PhoneMockup = () => (
  <div className="relative w-[280px] sm:w-[320px] rounded-[2rem] border border-white/15 bg-carbon p-3 shadow-[0_40px_120px_rgba(0,0,0,0.8)]">
    <div className="rounded-[1.5rem] bg-obsidian border border-white/10 overflow-hidden">
      <div className="px-5 pt-5 pb-4 border-b border-white/10">
        <div className="flex items-center justify-between">
          <span className="font-mono text-[10px] tracking-widest uppercase text-neutral-500">Today — You Have 30 Min</span>
          <Timer className="h-4 w-4 text-volt" />
        </div>
        <p className="font-display font-black text-3xl uppercase mt-2 leading-none">
          Express <span className="text-volt">Burn</span>
        </p>
        <div className="flex gap-1.5 mt-3">
          {["10", "20", "30", "40", "50"].map((m) => (
            <span
              key={m}
              className={`flex-1 text-center font-mono text-[10px] tracking-wider py-1.5 border ${
                m === "30" ? "bg-volt text-white border-volt font-semibold" : "border-white/15 text-neutral-500"
              }`}
            >
              {m}
            </span>
          ))}
        </div>
      </div>
      <div className="p-5 space-y-5">
        <div>
          <div className="flex justify-between font-mono text-[10px] tracking-widest uppercase text-neutral-500 mb-2">
            <span>Session Progress</span>
            <span className="text-volt">18:24 / 30:00</span>
          </div>
          <div className="h-2 rounded-full bg-white/10 overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: "61%" }}
              transition={{ duration: 1.4, delay: 1.6, ease: [0.16, 1, 0.3, 1] }}
              className="h-full bg-volt"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="border border-white/10 bg-carbon p-3">
            <p className="font-mono text-[9px] tracking-widest uppercase text-neutral-500">Intensity</p>
            <p className="font-display font-black text-2xl text-volt">86%</p>
          </div>
          <div className="border border-white/10 bg-carbon p-3">
            <p className="font-mono text-[9px] tracking-widest uppercase text-neutral-500">Intervals Left</p>
            <p className="font-display font-black text-2xl">07</p>
          </div>
        </div>
        <div>
          <p className="font-mono text-[9px] tracking-widest uppercase text-neutral-500 mb-2">Minutes Trained This Week</p>
          <div className="flex items-end gap-1.5 h-16">
            {[20, 10, 30, 20, 50, 10, 40].map((h, i) => (
              <motion.div
                key={i}
                initial={{ height: 0 }}
                animate={{ height: `${h * 2}%` }}
                transition={{ duration: 0.8, delay: 1.6 + i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                className={`flex-1 ${i === 4 ? "bg-volt" : "bg-white/15"}`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
    <div className="absolute -left-12 top-8 hidden sm:block">
      <motion.div
        animate={{ y: [-6, 6, -6] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
        className="bg-obsidian/90 backdrop-blur-md border border-volt/40 px-3 py-2 font-mono text-[10px] tracking-widest uppercase text-volt"
      >
        Rest Timer 0:45
      </motion.div>
    </div>
    <div className="absolute -right-8 bottom-24 hidden sm:block">
      <motion.div
        animate={{ y: [6, -6, 6] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        className="bg-obsidian/90 backdrop-blur-md border border-white/15 px-3 py-2 font-mono text-[10px] tracking-widest uppercase text-neutral-300"
      >
        Streak: 23 Days
      </motion.div>
    </div>
  </div>
);

export const Hero = () => {
  const tiltRef = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useTransform(my, [-0.5, 0.5], [9, -9]);
  const rotateY = useTransform(mx, [-0.5, 0.5], [-11, 11]);
  const { scrollYProgress } = useScroll();
  const bgY = useTransform(scrollYProgress, [0, 0.25], [0, 120]);

  const onMouseMove = (e) => {
    const r = tiltRef.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section id="top" data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden pt-[72px]">
      <img src="https://static.prod-images.emergentagent.com/jobs/753a59ae-6295-4a2c-a309-76467ea4b06e/images/26b4bed0e23b757d4eedc4e9991aed77d8e9189769dbc47ad0529fcc0f5d13cd.png" alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover opacity-25" />
      <motion.div style={{ y: bgY }} className="absolute inset-0 grid-lines" />
      <div className="absolute inset-0 hero-spotlight" />
      <motion.div
        style={{ y: bgY }}
        className="absolute -right-40 top-1/2 -translate-y-1/2 font-display font-black text-[26rem] leading-none text-stroke-volt opacity-[0.07] select-none hidden lg:block"
      >
        50
      </motion.div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid lg:grid-cols-12 gap-16 items-center w-full">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.35 }}
          >
            <Eyebrow>Early Access — Now Open</Eyebrow>
          </motion.div>

          <h1 className="font-display font-black uppercase tracking-tight leading-[0.9] text-5xl sm:text-6xl lg:text-7xl xl:text-[5.5rem] mt-6">
            <MaskLine delay={0.45}>The Only App That Fits Your Time.</MaskLine>
            <MaskLine delay={0.6}>
              <span className="text-volt italic">10 Minutes Or 50 — You Decide.</span>
            </MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.95, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 max-w-2xl text-base sm:text-lg text-neutral-400 leading-relaxed"
          >
            Fit50 is a complete fitness platform — coach-grade workouts from 10 to 50 minutes,
            500+ guided exercises, high-protein meal plans, and live heart rate coaching from
            your smartwatch. Pick your minutes. The rest is handled.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.1, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-get-started-button"
              onClick={() => scrollTo("#access")}
              className="group inline-flex items-center gap-3 bg-volt text-white font-display font-bold uppercase tracking-wide text-lg px-8 py-4 hover:bg-volt-hover hover:shadow-[0_0_35px_rgba(255,107,0,0.5)] hover:scale-[1.03] active:scale-[0.98] transition-[background-color,box-shadow,transform] duration-200"
            >
              Start Your First Workout
              <ArrowRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-see-inside-button"
              onClick={() => window.dispatchEvent(new Event("open-fit50-demo"))}
              className="inline-flex items-center gap-3 border border-white/25 bg-white/5 backdrop-blur-md text-neutral-100 font-display font-bold uppercase tracking-wide text-lg px-8 py-4 hover:border-volt hover:text-volt transition-colors duration-200"
            >
              <ArrowDown className="h-5 w-5" />
              See Everything Inside
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.3 }}
            className="mt-12 flex flex-wrap gap-3"
          >
            {CHIPS.map((c) => (
              <span
                key={c.label}
                className="inline-flex items-center gap-2 border border-white/15 bg-white/5 backdrop-blur-md px-4 py-2 font-mono text-[11px] tracking-widest uppercase text-neutral-300"
              >
                <c.icon className="h-3.5 w-3.5 text-volt" />
                {c.label}
              </span>
            ))}
          </motion.div>
        </div>

        <div className="lg:col-span-5 flex justify-center lg:justify-end">
          <motion.div
            initial={{ opacity: 0, y: 60, scale: 0.92 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
            style={{ perspective: 1200 }}
          >
            <motion.div
              ref={tiltRef}
              onMouseMove={onMouseMove}
              onMouseLeave={() => { mx.set(0); my.set(0); }}
              style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
            >
              <motion.div
                animate={{ y: [-8, 8, -8] }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              >
                <PhoneMockup />
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-2"
      >
        <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-neutral-500">Scroll</span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          className="w-px h-10 bg-gradient-to-b from-volt to-transparent"
        />
      </motion.div>
    </section>
  );
};
